import React, { useState, useEffect, useRef } from 'react';
import { NeonText, CyberButton, GlowingBorder } from './CyberEffects';
import './HowToPlayModal.css';

const STORAGE_KEY = 'five_league_tutorial_shown';

export function hasSeenTutorial() {
  return localStorage.getItem(STORAGE_KEY) === 'true';
}

export function markTutorialSeen() {
  localStorage.setItem(STORAGE_KEY, 'true');
}

// 各ページのナレーション音声ファイル（プリ生成MP3）
const NARRATION_AUDIO_FILES = [
  '/sounds/narration/tutorial-1.mp3',
  '/sounds/narration/tutorial-2.mp3',
  '/sounds/narration/tutorial-3.mp3',
  '/sounds/narration/tutorial-4.mp3',
  '/sounds/narration/tutorial-5.mp3',
];

function HowToPlayModal({ isOpen, onClose }) {
  const [page, setPage] = useState(0);
  const [isNarrating, setIsNarrating] = useState(false);
  const narrationRef = useRef(false);
  const audioRef = useRef(null);

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      narrationRef.current = false;
    };
  }, []);

  // モーダルが閉じたらナレーション停止
  useEffect(() => {
    if (!isOpen) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setIsNarrating(false);
      narrationRef.current = false;
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsNarrating(false);
    narrationRef.current = false;
    markTutorialSeen();
    setPage(0);
    onClose();
  };

  // 音声ファイルを再生してPromiseを返す
  const playAudioFile = (src) => {
    return new Promise((resolve) => {
      // 既存の音声を停止
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      const audio = new Audio(src);
      audioRef.current = audio;
      audio.onended = () => resolve();
      audio.onerror = () => resolve();
      audio.play().catch(() => resolve());
    });
  };

  // 特定ページの音声を再生
  const playPageAudio = (pageIndex) => {
    if (pageIndex >= 0 && pageIndex < NARRATION_AUDIO_FILES.length) {
      playAudioFile(NARRATION_AUDIO_FILES[pageIndex]);
    }
  };

  // ページ移動（ナレーション中は音声も切り替え）
  const nextPage = () => {
    setPage(p => {
      const newPage = p + 1;
      if (isNarrating && newPage < NARRATION_AUDIO_FILES.length) {
        playPageAudio(newPage);
      }
      return newPage;
    });
  };

  const prevPage = () => {
    setPage(p => {
      const newPage = p - 1;
      if (isNarrating && newPage >= 0) {
        playPageAudio(newPage);
      }
      return newPage;
    });
  };

  // 番組風に聞くボタン
  const startNarration = async () => {
    setIsNarrating(true);
    narrationRef.current = true;
    setPage(0);

    // 各ページを順番に再生
    for (let i = 0; i < NARRATION_AUDIO_FILES.length; i++) {
      if (!narrationRef.current) break;

      setPage(i);
      await playAudioFile(NARRATION_AUDIO_FILES[i]);

      if (!narrationRef.current) break;

      // ページ間の間
      await new Promise(r => setTimeout(r, 800));
    }

    setIsNarrating(false);
    narrationRef.current = false;
  };

  // ナレーション停止
  const stopNarration = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsNarrating(false);
    narrationRef.current = false;
  };

  // ページ1: イントロ
  const renderIntro = () => (
    <>
      <h2 className="how-to-play-title">
        <NeonText color="pink" size="lg">QUIZ</NeonText>
        <NeonText color="accent" size="lg" style={{ whiteSpace: 'nowrap' }}>ONE WORD</NeonText>
      </h2>

      <div className="intro-section">
        <p className="intro-tagline">
          <NeonText color="accent" size="md">テレビ番組に出演しているような体験を！</NeonText>
        </p>

        <div className="intro-description">
          <p>あの人気クイズ番組のような<strong>ドキドキ感</strong>を</p>
          <p>友達や家族と一緒に味わおう！</p>
        </div>

        <div className="intro-features">
          <div className="feature-item">
            <span className="feature-icon">📺</span>
            <span className="feature-text">テレビやPCで大画面表示</span>
          </div>
          <div className="feature-item">
            <span className="feature-icon">📱</span>
            <span className="feature-text">スマホがコントローラーに</span>
          </div>
          <div className="feature-item">
            <span className="feature-icon">🎉</span>
            <span className="feature-text">2〜6人で協力プレイ</span>
          </div>
        </div>
      </div>

      <div className="intro-buttons">
        {!isNarrating ? (
          <>
            <button className="narration-btn" onClick={startNarration}>
              🎙️ 番組風に聞く
            </button>
            <CyberButton variant="accent" className="how-to-play-btn" onClick={nextPage}>
              次へ →
            </CyberButton>
          </>
        ) : (
          <button className="narration-stop-btn" onClick={stopNarration}>
            ⏹️ 停止
          </button>
        )}
      </div>
    </>
  );

  // ページ2: ゲームの流れ
  const renderGameFlow = () => (
    <>
      <h2 className="how-to-play-title">
        <NeonText color="blue" size="lg" style={{ whiteSpace: 'nowrap' }}>ゲームの流れ</NeonText>
      </h2>

      <div className="game-flow-section">
        <div className="flow-step">
          <div className="flow-number">❶</div>
          <div className="flow-content">
            <div className="flow-title">ルームに集合</div>
            <div className="flow-desc">ホストがルームを作成し、コードを共有。全員参加したらスタート！</div>
          </div>
        </div>

        <div className="flow-step">
          <div className="flow-number">❷</div>
          <div className="flow-content">
            <div className="flow-title">問題が出題</div>
            <div className="flow-desc">クイズが表示され、答えの文字数分のマスが登場</div>
          </div>
        </div>

        <div className="flow-step">
          <div className="flow-number">❸</div>
          <div className="flow-content">
            <div className="flow-title">1人1文字を担当</div>
            <div className="flow-desc">各プレイヤーが担当の文字を<strong>制限時間内</strong>に選択！</div>
          </div>
        </div>

        <div className="flow-step">
          <div className="flow-number">❹</div>
          <div className="flow-content">
            <div className="flow-title">答え合わせ</div>
            <div className="flow-desc">全員の文字が揃って正解なら次の問題へ！</div>
          </div>
        </div>
      </div>

      <div className="nav-buttons">
        <CyberButton variant="secondary" className="how-to-play-btn-small" onClick={prevPage}>
          ← 戻る
        </CyberButton>
        <CyberButton variant="accent" className="how-to-play-btn-small" onClick={nextPage}>
          次へ →
        </CyberButton>
      </div>
    </>
  );

  // ページ3: 大画面表示の説明
  const renderTVMode = () => (
    <>
      <h2 className="how-to-play-title">
        <NeonText color="pink" size="lg" style={{ whiteSpace: 'nowrap' }}>📺 大画面モード</NeonText>
      </h2>

      <div className="tv-mode-section">
        <div className="tv-highlight">
          <NeonText color="accent" size="sm">まるでテレビ番組のスタジオ！</NeonText>
        </div>

        <div className="tv-description">
          <p>待機画面に表示される<strong>「観戦用リンク」</strong>を</p>
          <p>テレビやPCで開くと...</p>
        </div>

        <div className="tv-features">
          <div className="tv-feature">
            <span className="tv-icon">🎙️</span>
            <span className="tv-text">問題を音声で読み上げ！</span>
          </div>
          <div className="tv-feature">
            <span className="tv-icon">🎬</span>
            <span className="tv-text">番組さながらの演出</span>
          </div>
          <div className="tv-feature">
            <span className="tv-icon">👀</span>
            <span className="tv-text">全員の回答をリアルタイム表示</span>
          </div>
          <div className="tv-feature">
            <span className="tv-icon">🔥</span>
            <span className="tv-text">正解・不正解の迫力エフェクト</span>
          </div>
        </div>

        <div className="tv-tip recommended">
          <span className="tip-label">⭐ おすすめ</span>
          <span className="tip-text">PCで開いてテレビにミラーリング！大迫力で楽しもう！</span>
        </div>
      </div>

      <div className="nav-buttons">
        <CyberButton variant="secondary" className="how-to-play-btn-small" onClick={prevPage}>
          ← 戻る
        </CyberButton>
        <CyberButton variant="accent" className="how-to-play-btn-small" onClick={nextPage}>
          次へ →
        </CyberButton>
      </div>
    </>
  );

  // ページ4: ルール説明
  const renderGoal = () => (
    <>
      <h2 className="how-to-play-title">
        <NeonText color="accent" size="lg" style={{ whiteSpace: 'nowrap' }}>🎯 ゲームルール</NeonText>
      </h2>

      <div className="goal-section">
        <div className="goal-main">
          <div className="goal-text">最大</div>
          <div className="goal-number">
            <NeonText color="pink" size="xl">10</NeonText>
          </div>
          <div className="goal-text">問に挑戦！</div>
        </div>

        <div className="goal-rules">
          <div className="rule-item">
            <span className="rule-icon">📈</span>
            <span className="rule-text">問題ごとに<strong>難易度UP</strong>！</span>
          </div>
          <div className="rule-item">
            <span className="rule-icon">💀</span>
            <span className="rule-text"><strong>1問でも間違えたら終了</strong></span>
          </div>
          <div className="rule-item">
            <span className="rule-icon">⏱️</span>
            <span className="rule-text">全員で<strong>45秒</strong>以内に回答</span>
          </div>
          <div className="rule-item">
            <span className="rule-icon">🤝</span>
            <span className="rule-text">仲間を信じて<strong>自分の1文字</strong>に集中！</span>
          </div>
        </div>

        <div className="goal-example">
          <p className="example-title">例: 「りんご」の場合</p>
          <div className="example-players">
            <div className="example-player">
              <span className="player-label">Aさん</span>
              <span className="player-char">り</span>
            </div>
            <div className="example-player">
              <span className="player-label">Bさん</span>
              <span className="player-char">ん</span>
            </div>
            <div className="example-player">
              <span className="player-label">Cさん</span>
              <span className="player-char">ご</span>
            </div>
          </div>
          <p className="example-note">→ 全員正解で次の問題へ！</p>
        </div>
      </div>

      <div className="nav-buttons">
        <CyberButton variant="secondary" className="how-to-play-btn-small" onClick={prevPage}>
          ← 戻る
        </CyberButton>
        <CyberButton variant="accent" className="how-to-play-btn-small" onClick={nextPage}>
          次へ →
        </CyberButton>
      </div>
    </>
  );

  // ページ5: 準備完了
  const renderReady = () => (
    <>
      <h2 className="how-to-play-title">
        <NeonText color="pink" size="lg" style={{ whiteSpace: 'nowrap' }}>準備OK！</NeonText>
      </h2>

      <div className="ready-section">
        <div className="ready-checklist">
          <div className="check-item">
            <span className="check-icon">✓</span>
            <span className="check-text">2〜6人の仲間を集める</span>
          </div>
          <div className="check-item">
            <span className="check-icon">✓</span>
            <span className="check-text">全員スマホを用意</span>
          </div>
          <div className="check-item">
            <span className="check-icon">✓</span>
            <span className="check-text">できればテレビ/PCも用意</span>
          </div>
        </div>

        <div className="ready-message">
          <p>さあ、あなたも</p>
          <p><NeonText color="accent" size="md">クイズ番組の出演者</NeonText></p>
          <p>になろう！</p>
        </div>

        <div className="ready-cta">
          <p className="cta-text">🔥 何問連続で正解できるかな？ 🔥</p>
        </div>
      </div>

      <div className="nav-buttons">
        <CyberButton variant="secondary" className="how-to-play-btn-small" onClick={prevPage}>
          ← 戻る
        </CyberButton>
        <CyberButton variant="primary" className="how-to-play-btn-small start-btn" onClick={handleClose}>
          🎮 はじめる
        </CyberButton>
      </div>
    </>
  );

  const pages = [renderIntro, renderGameFlow, renderTVMode, renderGoal, renderReady];

  return (
    <div className="how-to-play-overlay" onClick={handleClose}>
      <div className="how-to-play-modal" onClick={(e) => e.stopPropagation()}>
        <GlowingBorder color="purple" className="how-to-play-content">
          {/* ページインジケーター */}
          <div className="page-indicator">
            {pages.map((_, idx) => (
              <span
                key={idx}
                className={`indicator-dot ${idx === page ? 'active' : ''} ${isNarrating ? 'disabled' : ''}`}
                onClick={() => !isNarrating && setPage(idx)}
              />
            ))}
          </div>

          {/* ナレーション中インジケーター */}
          {isNarrating && (
            <div className="narrating-indicator">🎙️ 読み上げ中...</div>
          )}

          {/* スキップボタン */}
          <button className="skip-btn" onClick={handleClose}>
            {isNarrating ? '停止して閉じる' : 'スキップ'}
          </button>

          {pages[page]()}
        </GlowingBorder>
      </div>
    </div>
  );
}

export default HowToPlayModal;
